import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Backdrop, Fade, CircularProgress } from "@mui/material";
import { Col, Row, FormControl } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  getBase64FromFile,
  convertTiffToJpegBlob,
  convertHeicToJpeg,
  extractExifFromBase64,
  validateImageExtension,
  getSafeInitialScale,
} from "../../Utils/imageUtils";

import {
  setImageData,
  setImageUrl,
  setBatchUrlImage,
  setLoader,
} from "../../Redux/MainImage/imageSlice";
import {
  setCreate_Template,
  setDiscarded,
  setBatchDownload,
  setID,
  setTemplateAuthoringAspectPreset,
} from "../../Redux/UserSlice/userSlice";
import { setItem, deleteAll } from "../../Redux/WaterMarks/watermarks";
import { setExtension } from "../../Redux/DownloadSlice/downloadSlice";

import helper_functions from "../../Config/helper_functions";
import CancelButton from "../CancelButton/Cancel_button";
import { GetAllTemplates, GetTemplateDataFromRedux } from "../../actions/Template/Template";
import { loadWatermarkRaster } from "../Canvas/canvasUtils";

import uploadIcon from "../../Assets/Images/pickImage.svg";
import linkIcon from "../../Assets/Images/linkurl.svg";

import "../../Assets/Styles/pages/uploadImage.scss";
import type { RootState } from "../../Redux/store";
import { usePremiumStatus } from "../../hooks/usePremiumStatus";
import PremiumGateDialog from "../Common/PremiumGateDialog";

/** Max photos a free user may have in ONE batch (total, not per upload). Exported so callers can
 *  block the "Add More Photos" entry point up front instead of only after files are picked. */
export const BATCH_LIMIT_FREE = 5;

interface ImageSourceSelectorModalProps {
  open: boolean;
  uploadImageType: "main" | "logo";
  setActive?: (value: string) => void;
  multiple?: boolean;
  onCloseAndClean: () => void;
  cause?: string;
  /** Batch Watermark page: uploaded photos should populate the on-page review grid instead of
   * jumping straight into the editor. When true, skips the auto `history.push("/edit")` below
   * and calls `onStayOnPage` (to just close this modal) once the upload finishes. */
  stayOnPage?: boolean;
  onStayOnPage?: () => void;
  /** Batch Watermark page's "Choose Photos"/"Add More Photos" buttons should open the native
   * OS file picker immediately, not this modal's own "pick a source" screen first. When true,
   * clicks the hidden file input as soon as the modal opens (the "computer" source is the only
   * one relevant for that flow anyway) -- the intro screen still renders underneath as a
   * fallback if the user cancels the native dialog. */
  autoTriggerFilePicker?: boolean;
  /** Files already picked outside this modal (e.g. dragged onto the page). When provided
   * alongside `autoTriggerFilePicker`, these are fed into the same hidden file input instead
   * of clicking it to open the native OS picker. */
  initialFiles?: FileList | null;
  /** Fired `true` when file processing starts and `false` when it finishes, so the caller can
   * block navigation/actions that would abandon a partially-uploaded batch. */
  onUploadingChange?: (uploading: boolean) => void;
}

const ImageSourceSelectorModal: React.FC<ImageSourceSelectorModalProps> = ({
  open,
  uploadImageType,
  setActive,
  multiple,
  onCloseAndClean,
  cause,
  stayOnPage,
  onStayOnPage,
  autoTriggerFilePicker,
  initialFiles,
  onUploadingChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const [urlInput, setUrlInput] = useState("");
  const [uploadTypeLoading, setUploadTypeLoading] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBatchLimitDialog, setShowBatchLimitDialog] = useState(false);
  const isPremium = usePremiumStatus();

  const { userState, mainImageState, templateSlice, waterMarksState } = useSelector((state: RootState) => ({
    userState: state.userSlice,
    mainImageState: state.mainImage,
    templateSlice: state.templateSlice,
    waterMarksState: state.watermarks,
  }));
  const uploadedImageUrl = mainImageState.url;

  const processSingleImageUpload = async (fileBlob, exifObj, extension) => {
    if (uploadImageType === "main") {
      dispatch(setLoader(true));
    }
    try {
      if (isOnline) {
        if (uploadImageType === "main") {
          dispatch(setExtension(extension));
          dispatch(setBatchDownload(false));

          const isNewTemplateFlow = cause === "newTemplate";
          // Template editing still relies on the stored square canvas reference so we keep
          // enforcing a square upload there. New-template creation no longer requires a
          // square image: the aspect bar lets the user shape the canvas and the save path
          // always remaps to 1:1 before serialising watermark data.
          // if (userState.edit_Template && !isNewTemplateFlow) {
          //   const square = await isSquareImage(fileBlob);
          //   if (!square) {
          //     message.error(
          //       "Please upload a 1:1 (square) image to update a template."
          //     );
          //     setUploadTypeLoading("");
          //     dispatch(setLoader(false));
          //     return;
          //   }
          // }

          // Only wipe watermark state when starting a brand-new template flow.
          // For "change image" while already authoring a template, keep existing items/properties.
          if (isNewTemplateFlow) {
            dispatch(setCreate_Template(true));
            dispatch(setTemplateAuthoringAspectPreset("1:1"));
            dispatch(deleteAll());
            dispatch(setID(null));
          }

          // If user selected a template from `/home`, apply it immediately for single-image upload.
          // IMPORTANT: do NOT populate `mainImageState.batchurls`, otherwise the UI switches to "batch" mode.
          const rawTempId = userState.current_TemplateID;
          const templateObj =
            typeof rawTempId === "object" && rawTempId !== null ? rawTempId : null;
          const landscapeId = templateObj ? (templateObj as { landcsapeTemp?: string }).landcsapeTemp : undefined;
          const portraitId = templateObj ? (templateObj as { portraitTemp?: string }).portraitTemp : undefined;
          const templateId = portraitId || landscapeId;

          dispatch(
            setImageUrl({
              url: URL.createObjectURL(fileBlob),
              name: fileBlob.name,
              metadata: JSON.stringify(exifObj),
            })
          );

          // Only auto-apply selected templates when NOT creating a new-template flow.
          if (templateId && !isNewTemplateFlow && !userState.create_Template) {
            const templates = templateSlice as unknown as { templates?: Array<{ id: string }> };
            const hasTemplate = Array.isArray(templates?.templates)
              ? templates.templates.some((t) => t?.id === templateId)
              : false;

            // If template list isn't loaded yet (slow fetch), load it once and retry.
            if (!hasTemplate) {
              try {
                await dispatch(GetAllTemplates() as any);
              } catch {
                // If fetching fails, GetTemplateDataFromRedux will no-op with an error log.
              }
            }

            void dispatch(
              GetTemplateDataFromRedux(
                templateId,
                (templateSlice as any) ?? ({} as any),
                waterMarksState,
                false
              )
            );
          }
          setUploadTypeLoading("");
        } else if (uploadImageType === "logo" && mainImageState.url) {
          let effectiveBlob = fileBlob;
          // SVGs rasterize at low intrinsic size in canvas/Konva.
          // Convert to a higher-res PNG (admin-style) so scaling up stays crisp.
          if (helper_functions.isSvgFile(fileBlob)) {
            try {
              effectiveBlob = await helper_functions.convertSvgFileToHighResPng(fileBlob, 4);
            } catch (e) {
              console.error("SVG conversion failed, using original SVG", e);
              effectiveBlob = fileBlob;
            }
          }

          const scale = await getSafeInitialScale(effectiveBlob, 0.25);
          const rasterSrc = URL.createObjectURL(effectiveBlob);
          try {
            await loadWatermarkRaster(rasterSrc);
          } catch (e) {
            console.error("Failed to pre-decode watermark raster", e);
          }

          dispatch(
            setItem({
              type: "image",
              property: "image",
              id: String(Date.now()),
              x: 50,
              y: 50,
              deg: 0,
              scaleX: scale,
              scaleY: scale,
              src: rasterSrc,
              opacity: 1,
              src_type: effectiveBlob.type,
              name: effectiveBlob.name,
              size: effectiveBlob.size,
            })
          );
          if (setActive) setActive("image");

          // Keep the "Processing image..." overlay up until the canvas stage has committed the new watermark + transformer
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
          });

          onCloseAndClean();
        }
      }
    } finally {
      if (uploadImageType === "main") {
        dispatch(setLoader(false));
      }
    }
  };

  const handleUrlUpload = async () => {
    setUploadTypeLoading("link");
    if (!urlInput) {
      alert(t("ImageUrlError"));
      setUploadTypeLoading("");
      return;
    }
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    const isValidUrl = urlRegex.test(urlInput);
    if (!isValidUrl) {
      alert("The URL appears to be invalid!");
      setUrlInput("");
      setUploadTypeLoading("");
      return;
    }
    try {
      const response = await fetch(urlInput);
      const blob = await response.blob();
      const fileType = blob.type.split("/")[1];
      const allowedTypes = ["jpeg", "png", "svg+xml"];
      if (!allowedTypes.includes(fileType)) {
        throw new Error("Only JPEG, PNG, and SVG formats are supported.");
      }
      // if (userState.edit_Template) {
      //   Promise.all([
      //     dispatch(setEdit_Template(false)),
      //     dispatch(setCreate_Template(true)),
      //   ]);
      // }
      const extension = fileType === "svg+xml" ? "png" : fileType;
      const fileFromBlob = new File([blob], `image.${extension}`, { type: blob.type });
      await processSingleImageUpload(fileFromBlob, {}, extension);
    } catch (error) {
      setUrlInput("");
      alert(
        `Error loading the image: ${
          error.message === "Failed to fetch"
            ? "Not image url or CORS issue."
            : error.message
        }`
      );
    } finally {
      setUploadTypeLoading("");
    }
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadTypeLoading("file");
    let selectedFiles = Array.from(event.target.files);
    if (!selectedFiles.length) {
      setUploadTypeLoading("");
      return;
    }

    // Free tier caps the TOTAL batch size, not one pick. Counting only `selectedFiles.length`
    // let "Add More Photos" bypass it entirely -- 5 already uploaded plus 5 more each passed the
    // check individually while the batch grew past the limit. Matches iOS, whose
    // `isPhotosLimited()` tests `selectedAssetsArr.count > 5` against the whole accumulated
    // selection (WMBulkHomeViewController).
    if (
      multiple &&
      !isPremium &&
      (mainImageState.batchurls?.length ?? 0) + selectedFiles.length >
        BATCH_LIMIT_FREE
    ) {
      setUploadTypeLoading("");
      setShowBatchLimitDialog(true);
      return;
    }

    // Let the caller block navigation/other actions for as long as this loop runs -- decoding
    // and converting a large batch takes a while, and leaving the page mid-way abandons it
    // with only some images added.
    onUploadingChange?.(true);

    // EWW-448: `setUploadTypeLoading("file")` above renders the processing overlay, but the work
    // below (heic2any in particular, plus base64/EXIF on large files) is synchronous on the main
    // thread -- so React commits the overlay to the DOM and the browser then never gets a frame
    // to actually paint it before blocking. The spinner existed but was invisible for the entire
    // wait, which is exactly the "no loader" report. Yield two frames first so the overlay is on
    // screen before we block. Double rAF because a single one still fires before paint.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    // Batch mode is determined by the caller's `multiple` prop, not by how many files happen
    // to be picked in a given interaction -- "Add more photos" on an existing batch very
    // commonly selects just one file, which must still append to the batch rather than
    // falling through to the single-image replace path below.
    const allowMultiple = Boolean(multiple);
    const rawTempId = userState.current_TemplateID;
    const { landcsapeTemp: landscapeId = undefined, portraitTemp: portraitId = undefined } =
      typeof rawTempId === "object" && rawTempId !== null ? rawTempId : {};
    let successfulUploads = 0;

    for (let file of selectedFiles) {
      let ext = validateImageExtension(file.name);
      if (!ext) {
        dispatch(setDiscarded(true));
        continue;
      }

      if (ext === "tiff") {
        try {
          const tiffBlob = await convertTiffToJpegBlob(file);
          file = new File([tiffBlob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: tiffBlob.type });
          ext = "jpeg";
        } catch (err) {
          console.error("TIFF conversion error", err);
          continue;
        }
      }

      if (ext === "heic" || ext === "heif") {
        try {
          file = await convertHeicToJpeg(file);
          // A `.HEIC` that was really JPEG bytes now comes back renamed to `.jpg`.
          ext = validateImageExtension(file.name) || "jpeg";
        } catch (err) {
          // Was a bare `continue` -- a real conversion failure vanished with no feedback at
          // all. Flag it so the existing "unsupported format" notice fires instead.
          console.error("HEIC conversion error", err);
          dispatch(setDiscarded(true));
          continue;
        }
      }

      const base64Raw = await getBase64FromFile(file);
      const base64 = typeof base64Raw === "string" ? base64Raw : "";
      const exifData = extractExifFromBase64(base64);

      if (!allowMultiple) {
        if (!ext) {
          alert(t("NotSuportedFile"));
        } else {
          if (ext === "svg") ext = "png";
          if (ext === "gif") {
            const image = new window.Image();
            image.src = base64;
            await new Promise<void>((resolve) => {
              image.onload = async () => {
                const resized = await helper_functions.resizeFile(
                  base64,
                  image.width,
                  image.height,
                  "jpeg",
                  100
                );
                const resizedFile = helper_functions.dataURLtoFile(resized, "image.jpg");
                if (resizedFile) {
                  file = resizedFile;
                  ext = "jpeg";
                  await processSingleImageUpload(file, exifData, ext);
                }
                resolve();
              };
            });
          } else {
            await processSingleImageUpload(file, exifData, ext);
          }
        }
      } else {
        successfulUploads++;
        dispatch(setExtension(ext));

        if (ext === "svg") ext = "1";

        if (ext === "gif") {
          const image = new window.Image();
          image.src = base64;
          image.onload = async () => {
            const resized = await helper_functions.resizeFile(
              base64,
              image.width,
              image.height,
              "jpeg",
              100
            );
            const gifFile = helper_functions.dataURLtoFile(resized, "image.jpg");
            if (gifFile) {
              ext = "jpeg";
              dispatch(
                setImageData({
                  url: URL.createObjectURL(gifFile),
                  name: file.name,
                  metadata: JSON.stringify(exifData),
                  landcsapeTemp: landscapeId,
                  portraitTemp: portraitId,
                  extension: ext,
                })
              );
            }
          };
        } else {
          dispatch(
            setImageData({
              url: URL.createObjectURL(file),
              name: file.name,
              metadata: JSON.stringify(exifData),
              landcsapeTemp: landscapeId,
              portraitTemp: portraitId,
              extension: ext,
            })
          );
        }
      }
    }

    if (successfulUploads) dispatch(setBatchUrlImage(0));

    onUploadingChange?.(false);

    // Close and return to the calling page directly once uploads finish, rather than relying
    // on the `uploadedImageUrl` effect below -- on a second/third "add more" round,
    // `setBatchUrlImage(0)` always points at the same first photo, so that value doesn't
    // change and the effect never re-fires.
    if (stayOnPage && successfulUploads > 0) onStayOnPage?.();

    // if (userState.edit_Template) {
    //   dispatch(setEdit_Template(false));
    //   dispatch(setCreate_Template(true));
    // }

    setUploadTypeLoading("");
  };

  /**
   * My Templates → edit flow: skip upload and use the same demo background as new-template
   * creation. Template watermarks are already in Redux from `GetTemplateData`; do not
   * `deleteAll` or toggle `create_Template`. Navigate here because this modal unmounts
   * before the `uploadedImageUrl` effect can run.
   */

  // Safari: must be `useLayoutEffect`, not `useEffect`.
  //
  // Safari only honours a programmatic `.click()` on a file input while the user-activation
  // from the originating gesture is still live. React 17 flushes a state update made in an
  // event handler synchronously within that same event, and layout effects run before the
  // browser is handed back control -- so clicking from here is still inside the gesture. A
  // plain `useEffect` runs after paint, by which point Safari has dropped the activation and
  // silently ignores the click: "Choose Image" did nothing at all on /home, and on
  // /batch-watermark only a double click worked (the second click landing while the previous
  // instance was still mounted). Chrome is lenient about this, which is why it only showed up
  // on Safari.
  useLayoutEffect(() => {
    if (!(open && autoTriggerFilePicker)) return;
    const input = fileInputRef.current;
    if (!input) return;
    if (initialFiles && initialFiles.length) {
      // Files already picked (e.g. dropped onto the page) -- feed them into this same input
      // and let its normal onChange handler process them, instead of opening the OS picker.
      const dt = new DataTransfer();
      Array.from(initialFiles).forEach((f) => dt.items.add(f));
      input.files = dt.files;
      input.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }
    input.click();
    // If the user dismisses the OS file dialog WITHOUT picking a file, no `change` event fires,
    // so `open` stays true and this modal lingers (invisibly, since the autoTrigger branch
    // renders no visible UI) -- the next "Choose Image" click sets `open` to the same `true`,
    // this effect doesn't re-run, and the picker never reopens. The `cancel` event fires exactly
    // on that dismissal; reset the open state so a subsequent click mounts fresh and re-triggers.
    const handleCancel = () => onCloseAndClean();
    input.addEventListener("cancel", handleCancel);
    return () => input.removeEventListener("cancel", handleCancel);
    // onCloseAndClean is a stable close handler (sets state false); intentionally excluded so a
    // new identity can't re-run this effect and reopen the picker unexpectedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoTriggerFilePicker]);

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(true);
    const handleOfflineStatus = () => setIsOnline(false);

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
    };
  }, [navigator.onLine]);

  useEffect(() => {
    if (uploadImageType === "main" && uploadedImageUrl) {
      if (location.pathname === "/edit" || stayOnPage) return;
      if (cause === "newTemplate") {
        dispatch(setCreate_Template(true));
      }
      history.push("/edit");
    }
  }, [uploadedImageUrl, uploadImageType, history, location.pathname, cause, dispatch, stayOnPage]);

  // Batch Watermark's "Choose Photos"/"Add More Photos": open the native OS file picker
  // directly with no visible intro screen at all -- the Modal/Backdrop/Fade below exist only
  // to present that intro screen, so skip mounting them entirely rather than just hiding them
  // (avoids a visible fade-in flash while the native dialog is still opening).
  if (autoTriggerFilePicker) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          id="files"
          // `.hidden` is actually SCSS-nested as `.right .hidden` (see the visible-modal
          // branch below), so it only hides this input when rendered inside that ancestor.
          // This branch renders no such wrapper, so hide it explicitly instead.
          style={{ display: "none" }}
          onChange={handleFileInputChange}
          accept="image/*"
          multiple={multiple}
        />
        {/* EWW-410: this branch deliberately renders no intro screen, so until now nothing at
            all appeared between picking a file and landing in the editor -- but decoding is
            slow (HEIC/TIFF conversion, base64, EXIF), and a large photo left the page looking
            frozen. The visible-modal branch below already shows a spinner via
            `uploadTypeLoading`; mirror it here as a lightweight overlay so the wait is
            acknowledged instead of silent. */}
        {/* EWW-448: portalled to <body> for the same reason as the editor's overlay -- rendered
            in place (deep inside nav -> sidebar -> .editor-page) it does not reliably paint,
            because an ancestor creates a containing block for `position: fixed` at runtime. */}
        {uploadTypeLoading === "file" &&
          createPortal(
            <div className="image-upload-processing-overlay">
              <CircularProgress className="themeColor" />
              <span>Processing image…</span>
            </div>,
            document.body
          )}
        <PremiumGateDialog
          open={showBatchLimitDialog}
          onClose={() => {
            setShowBatchLimitDialog(false);
            onCloseAndClean();
          }}
          title="Batch Limit Reached"
          message={`Free users can process up to ${BATCH_LIMIT_FREE} images in a batch. Upgrade to Pro to process unlimited images.`}
        />
      </>
    );
  }

  return (
    <>
    <Modal
      open={open}
      onClose={() => {
        onCloseAndClean();
        if (uploadImageType === "logo" && setActive) setActive("");
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 800,
      }}
    >
      <Fade in={open}>
        <Row className="model uploadModal m-0">
          <CancelButton close={() => onCloseAndClean()} />
          <Col className="right mt-5 pt-2">
            <img className="uploadLink" src={uploadIcon} alt={uploadIcon} />
            <Row className="text_18 mt-2 productSansBold">
              {t("upload.Select")}
            </Row>
            <Row className="text_15 mb-3 text-center productSansLight mt-2 mb-2">
              {t("upload.SelectDisc")}
            </Row>
            <input
              ref={fileInputRef}
              type="file"
              id="files"
              className="hidden"
              onChange={handleFileInputChange}
              accept="image/*"
              multiple={multiple}
            />
            <label
              htmlFor="files"
              className={` ${uploadTypeLoading === "file" && "loaderOpacity"} ${
                multiple && "mb-5"
              } upload_label productSansLight pointer text_center text_15`}
            >
              {uploadTypeLoading === "file" ? (
                <CircularProgress className="themeColor signInLoader" />
              ) : multiple ? (
                "Upload Images"
              ) : (
                t("UploadImage")
              )}
            </label>
          </Col>
          {!multiple && (
            <>
              <Row className="w-100 m-0 my-2 py-2 d-flex align-items-center justify-content-center">
                <div className="borderUpload w_40"></div>
                <div className="productSansLight w_10 text_16 text-center p-0 mx-2">
                  {" "}
                  OR{" "}
                </div>
                <div className="borderUpload w_40"></div>
              </Row>
              <Col className="right px-0 mb-3">
                <img className="uploadLink" src={linkIcon} alt={linkIcon} />
                <Row className="text_18 productSansBold mt-2">
                  {t("EnterUrl")}
                </Row>
                <Row className="text_15 productSansLight  text-wrap w_83_xl d-flex justify-content-center text-center mt-2 mb-2">
                  {t("EnterUrlDisc")}
                </Row>{" "}
                <Row className="w_83_xl w-100 my- mt-1">
                  <FormControl
                    required
                    className="bg_link py-2 productSansLight text_14 mb-3"
                    type="text"
                    placeholder="http://www.example.com/examplefile"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                  />
                </Row>
                <label
                  onClick={handleUrlUpload}
                  className={` ${
                    uploadTypeLoading === "link" && "loaderOpacity"
                  } upload_label productSansLight pointer text_center text_15 mt-2 mb-4`}
                >
                  {uploadTypeLoading === "link" ? (
                    <CircularProgress className="themeColor signInLoader" />
                  ) : (
                    t("AddUrl")
                  )}
                </label>
              </Col>
            </>
          )}
        </Row>
      </Fade>
    </Modal>
    <PremiumGateDialog
      open={showBatchLimitDialog}
      onClose={() => setShowBatchLimitDialog(false)}
      title="Batch Limit Reached"
      message={`Free users can process up to ${BATCH_LIMIT_FREE} images in a batch. Upgrade to Pro to process unlimited images.`}
    />
    </>
  );
};

export default ImageSourceSelectorModal;
